import { Injectable, inject } from "@angular/core";
import { Router } from "@angular/router";

@Injectable({
  providedIn: "root"
})
export class BookingNavigationService {
  private router = inject(Router);

  private readonly STORAGE_KEY = "booking_original_route";
  private readonly PARAMS_STORAGE_KEY = "booking_original_route_params";

  /**
   * Store the original route that led to the booking process
   * Uses sessionStorage to persist across page refreshes
   */
  setOriginalRoute(route: string, params?: any): void {
    try {
      sessionStorage.setItem(this.STORAGE_KEY, route);
      if (params) {
        sessionStorage.setItem(this.PARAMS_STORAGE_KEY, JSON.stringify(params));
      } else {
        sessionStorage.removeItem(this.PARAMS_STORAGE_KEY);
      }
      console.log(
        "BookingNavigationService: Stored original route:",
        route,
        params
      );
    } catch (error) {
      console.warn(
        "BookingNavigationService: Failed to store route in sessionStorage:",
        error
      );
    }
  }

  /**
   * Get the stored original route from sessionStorage
   */
  getOriginalRoute(): { route: string | null; params: any } {
    try {
      const route = sessionStorage.getItem(this.STORAGE_KEY);
      const paramsStr = sessionStorage.getItem(this.PARAMS_STORAGE_KEY);
      const params = paramsStr ? JSON.parse(paramsStr) : null;

      return {
        route,
        params
      };
    } catch (error) {
      console.warn(
        "BookingNavigationService: Failed to retrieve route from sessionStorage:",
        error
      );
      return { route: null, params: null };
    }
  }

  /**
   * Navigate back to the original route that started the booking process
   */
  returnToOriginalRoute(): void {
    const { route, params } = this.getOriginalRoute();

    if (route) {
      console.log(
        "BookingNavigationService: Returning to original route:",
        route
      );
      // If the stored route contains query / fragment / matrix params inline, use navigateByUrl
      // Using router.navigate([route]) with a string containing '?' causes Angular to encode it as part of the path (e.g. 'view%3Fv%3DVisit')
      if (/[?#;]/.test(route)) {
        this.router.navigateByUrl(route).catch((err) => {
          console.warn(
            "BookingNavigationService: navigateByUrl failed, falling back to root:",
            err
          );
          this.router.navigate(["/"]);
        });
      } else {
        if (params) {
          this.router.navigate([route], params).catch((err) => {
            console.warn(
              "BookingNavigationService: navigate with params failed, falling back to root:",
              err
            );
            this.router.navigate(["/"]);
          });
        } else {
          this.router.navigate([route]).catch((err) => {
            console.warn(
              "BookingNavigationService: navigate failed, falling back to root:",
              err
            );
            this.router.navigate(["/"]);
          });
        }
      }
    } else {
      console.log(
        "BookingNavigationService: No original route stored, using fallback navigation"
      );
      // Fallback to a safe default if no route was stored
      this.router.navigate(["/"]);
    }
  }

  /**
   * Clear the stored route (e.g., when navigation is complete)
   * Removes from sessionStorage
   */
  clearOriginalRoute(): void {
    try {
      sessionStorage.removeItem(this.STORAGE_KEY);
      sessionStorage.removeItem(this.PARAMS_STORAGE_KEY);
      console.log("BookingNavigationService: Cleared original route");
    } catch (error) {
      console.warn(
        "BookingNavigationService: Failed to clear route from sessionStorage:",
        error
      );
    }
  }

  /**
   * Check if an original route is stored in sessionStorage
   */
  hasOriginalRoute(): boolean {
    try {
      return sessionStorage.getItem(this.STORAGE_KEY) !== null;
    } catch (error) {
      console.warn(
        "BookingNavigationService: Failed to check sessionStorage:",
        error
      );
      return false;
    }
  }
}
